type EventName = 'page_view' | 'link_click' | 'start_fit_check' | 'submit_fit_check'
export type EnquiryAction = 'view' | 'start' | 'step_view' | 'step_complete' | 'validation_error' | 'submit_attempt' | 'submit_failure' | 'success'
export type AnalyticsChoice = 'essential' | 'analytics' | 'measurement'
declare global { interface Window {
  _paq?: unknown[][]
  initialiseGoogleAnalytics?: (pageLocation: string, pageTitle: string, pageReferrer: string) => void
  trackGoogleEnquiry?: () => void
  trackGoogleEnquiryAction?: (action: EnquiryAction, step: number) => void
  'ga-disable-G-7KH2W9M6RJ'?: boolean
} }
const choiceKey = 'pc_cookie_choice'
const base = '/admin/matomo/'
let lastPage = ''
let lastGooglePage = ''
let lastGoogleChoice = ''
export function analyticsAllowed() {
  try { return ['analytics', 'measurement'].includes(localStorage.getItem(choiceKey) ?? '') } catch { return false }
}
function queue(...command: unknown[]) { (window._paq ??= []).push(command) }
function initialise() {
  if (!analyticsAllowed() || location.pathname.startsWith('/admin')) return false
  if (!document.getElementById('matomo-tracker')) {
    queue('requireConsent')
    queue('setTrackerUrl', `${location.origin}${base}matomo.php`)
    queue('setSiteId', '1')
    queue('setCookieDomain', '*.chinacare.cc'); queue('setCookiePath', '/')
    queue('setVisitorCookieTimeout', 90 * 24 * 60 * 60); queue('setSecureCookie', true)
    queue('setRequestMethod', 'POST')
    queue('enableHeartBeatTimer', 15)
    try { if (document.referrer) queue('setReferrerUrl', new URL(document.referrer).origin) } catch { /* Invalid referrer. */ }
    queue('setConsentGiven')
    const script = document.createElement('script')
    script.id = 'matomo-tracker'; script.async = true; script.src = `${base}matomo.js`
    document.head.appendChild(script)
  }
  return true
}
function pageUrl(path: string) {
  const url = new URL(path, location.origin)
  const tags = new URLSearchParams()
  const query = new URLSearchParams(location.search)
  for (const name of ['utm_source', 'utm_medium', 'utm_campaign']) {
    const value = query.get(name)
    if (value && /^[\p{L}\p{N}_. -]{1,100}$/u.test(value)) tags.set(name, value)
  }
  return `${url.origin}${url.pathname}${tags.size ? `?${tags}` : ''}`
}
export function trackEvent(eventName: EventName, metadata: Record<string, string> = {}) {
  if (eventName === 'start_fit_check') return trackEnquiry('start', 1)
  if (eventName === 'submit_fit_check') return trackEnquiry('success', 3)
  try {
    if (!trackPage(location.pathname)) return false
    if (eventName === 'link_click') queue('trackEvent', 'Links', `${location.pathname} · ${metadata.placement}`, `${metadata.label} → ${metadata.destination}`)
    return true
  } catch { return false /* Analytics must never block navigation or the form. */ }
}
export function trackEnquiry(action: EnquiryAction, step: number) {
  if (!['view', 'start', 'step_view', 'step_complete', 'validation_error', 'submit_attempt', 'submit_failure', 'success'].includes(action) || ![1, 2, 3].includes(step)) return false
  try {
    if (!trackPage(location.pathname)) return false
    // Only fixed action names and a step number; never accept form metadata.
    queue('trackEvent', 'Enquiry', action, `step_${step}`)
    try { window.trackGoogleEnquiryAction?.(action, step) } catch { /* Independent collectors. */ }
    if (action === 'success') {
      try { window.trackGoogleEnquiry?.() } catch { /* The receipt is already successful. */ }
    }
    return true
  } catch { return false }
}
export function trackPage(path: string) {
  if (!analyticsAllowed() || location.pathname.startsWith('/admin')) return false
  let referrer = ''
  try { if (document.referrer) referrer = new URL(document.referrer).origin } catch { /* Invalid referrer. */ }
  try {
    const choice = localStorage.getItem(choiceKey) ?? ''
    if (window.initialiseGoogleAnalytics && (lastGooglePage !== path || lastGoogleChoice !== choice)) {
      window.initialiseGoogleAnalytics(pageUrl(path), document.title, referrer)
      lastGooglePage = path; lastGoogleChoice = choice
    }
  } catch { /* Matomo remains independent. */ }
  try {
    if (!initialise()) return false
    if (lastPage !== path) {
      queue('setCustomUrl', pageUrl(path))
      queue('setDocumentTitle', document.title)
      queue('trackPageView', document.title)
      lastPage = path
    }
    return true
  } catch { return false }
}
export function trackLink(event: MouseEvent) {
  if (event.type === 'auxclick' && event.button !== 1) return
  const target = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null
  if (!target || target.hasAttribute('data-no-analytics')) return
  try {
    const url = new URL(target.href, location.origin)
    const destination = ['http:', 'https:'].includes(url.protocol)
      ? `${url.origin === location.origin ? '' : url.origin}${url.pathname}` : url.protocol
    trackEvent('link_click', { destination,
      label: (target.dataset.analytics ?? target.getAttribute('aria-label') ?? target.textContent ?? '').trim().slice(0, 100),
      placement: target.closest('header') ? 'header' : target.closest('footer') ? 'footer'
        : target.closest('.floating-actions') ? 'floating' : 'content' })
  } catch { /* Ignore invalid links. */ }
}
export function setAnalyticsChoice(value: AnalyticsChoice) {
  try {
    localStorage.setItem(choiceKey, value)
    localStorage.removeItem('pc_visitor')
    sessionStorage.removeItem('pc_session'); sessionStorage.removeItem('pc_attribution')
    if (value !== 'measurement') {
      for (const item of document.cookie.split(';')) {
        const name = item.trim().split('=')[0]
        if (name.startsWith('_gcl_')) {
          for (const domain of ['', '; domain=.chinacare.cc']) document.cookie = `${name}=; Max-Age=0; path=/${domain}; Secure; SameSite=Lax`
        }
      }
    }
    if (value === 'essential') {
      window['ga-disable-G-7KH2W9M6RJ'] = true
      queue('forgetConsentGiven'); queue('deleteCookies')
      // Remove cookies even when the tracker is blocked or still loading.
      for (const item of document.cookie.split(';')) {
        const name = item.trim().split('=')[0]
        if (/^(_pk_|mtm_consent|_ga(?:_|$))/.test(name)) {
          for (const domain of ['', '; domain=.chinacare.cc']) document.cookie = `${name}=; Max-Age=0; path=/${domain}; Secure; SameSite=Lax`
        }
      }
      location.reload()
    } else if (value === 'analytics' && document.getElementById('google-analytics-tracker')) {
      // Reload so a revoked advertising choice applies before any further tag activity.
      location.reload()
    } else window.dispatchEvent(new Event('analytics-choice'))
  } catch { /* Storage can be disabled by the visitor. */ }
}
